<template>
  <v-app>
    <v-main style="background-color: #fff">
      <div class="print-page">
        <!-- Controls: on screen only, never on paper -->
        <div class="no-print mb-6">
          <v-card variant="flat" border>
            <v-card-text>
              <div class="d-flex flex-wrap align-center" style="gap: 16px">
                <v-select
                  v-model="show"
                  :items="showOptions"
                  label="Näita"
                  hide-details
                  style="max-width: 210px"
                  variant="outlined"
                  density="compact"
                />
                <date-field
                  v-model="from"
                  label="Alates"
                  hide-details
                  clearable
                  style="max-width: 190px"
                  variant="outlined"
                  density="compact"
                />
                <date-field
                  v-model="to"
                  label="Kuni"
                  hide-details
                  clearable
                  style="max-width: 190px"
                  variant="outlined"
                  density="compact"
                />
                <v-checkbox
                  v-model="includePhotos"
                  label="Kaasa fotod"
                  hide-details
                  class="mt-0 pt-0"
                  density="compact"
                />
                <v-spacer />
                <v-btn @click="$router.back()" variant="text">Tagasi</v-btn>
                <v-btn color="primary" :disabled="loading" @click="print">
                  <v-icon start>mdi-printer</v-icon>Prindi
                </v-btn>
              </div>
              <div v-if="includePhotos && !photosReady && photoCount" class="text-caption text-grey mt-2">
                Fotode laadimine… ({{photosLoaded}}/{{photoCount}})
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- The printable document -->
        <div class="document">
          <div class="doc-header">
            <img :src="logo" class="doc-logo" />
            <div class="doc-title">
              <h1>{{title}}</h1>
              <div class="doc-meta">{{$address(object.fullAddress)}}</div>
              <div class="doc-meta">{{periodLabel}}</div>
              <div class="doc-meta">Väljastatud: {{today}}</div>
            </div>
          </div>

          <loading-content :loading="loading">
            <div v-if="!notes.length" class="text-grey py-4">Valitud perioodil kirjeid ei ole.</div>
            <div v-for="note in notes" :key="note.id" class="entry">
              <div class="entry-head">
                <strong>{{format(note.entryDate)}}</strong>
                <span class="entry-flags">
                  <!-- The office sees notes the customer never will, and this
                       page is the office's own paper. Say so on the row, so a
                       hidden entry is never handed over unawares. -->
                  <span v-if="note.hiddenFromClient" class="entry-hidden">Kliendile peidetud</span>
                  <!-- A töökäsk row carries a comment but no service flag, so it
                       is named by its type rather than answered Jah/Ei. -->
                  <template v-if="note.type === 'calendar'">Töökäsk</template>
                  <template v-else>Hooldus: {{note.service ? 'Jah' : 'Ei'}}</template>
                </span>
                <span class="entry-creator">{{(note.creator && note.creator.fullName) || '—'}}</span>
              </div>
              <div class="entry-body notes" v-html="safe(note.description)" />
              <div v-if="includePhotos && (note.photos || []).length" class="entry-photos">
                <img
                  v-for="pic in note.photos"
                  :key="round + ':' + pic"
                  :src="photoUrl(note.id, pic)"
                  class="entry-photo"
                  @load="onPhotoSettled"
                  @error="onPhotoSettled"
                />
              </div>
            </div>
            <div v-if="notes.length" class="doc-footer">Kirjeid kokku: {{notes.length}}</div>
          </loading-content>
        </div>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import api from '@/api'
import { mapActions } from 'vuex'
import { pickBy } from 'lodash'
import dayjs from 'dayjs'
import logo from '@/assets/gaspre-logo.png'
import { sanitizeHtml } from '@/plugins/sanitize'

// What the Näita filter sends as `service` and what the document then calls
// itself. `undefined` drops out of the query, which is how the endpoint is
// asked for everything.
const SHOW = {
  all: { service: undefined, title: 'Märkuste ja hoolduste ajalugu' },
  service: { service: 1, title: 'Hoolduste ajalugu' },
  notes: { service: 0, title: 'Märkuste ajalugu' }
}

export default {
  data () {
    return {
      logo,
      object: {},
      notes: [],
      loading: true,
      includePhotos: true,
      photosLoaded: 0,
      // Goes up with every load of the list and is part of each photo's key:
      // a photo that stayed through a filter change kept its <img>, fired no
      // second `load`, and the count that was just zeroed never caught up —
      // Prindi then said "Fotod laevad veel" for good.
      round: 0,
      show: 'all',
      from: '',
      to: '',
      showOptions: [
        { text: 'Kõik', value: 'all' },
        { text: 'Ainult hooldused', value: 'service' },
        { text: 'Ainult märkused', value: 'notes' }
      ]
    }
  },
  computed: {
    objectId () {
      return this.$route.params.objectId
    },
    selected () {
      return SHOW[this.show] || SHOW.all
    },
    title () {
      return this.selected.title
    },
    params () {
      return pickBy({
        service: this.selected.service,
        from: this.from || undefined,
        to: this.to || undefined
        // One document, not a page of it: refresh() pages through the whole
        // history via getAllObjectNotes, so no limit/offset here.
      }, v => v !== undefined)
    },
    today () {
      return dayjs().format('DD.MM.YYYY')
    },
    periodLabel () {
      if (this.from && this.to) {
        return 'Periood: ' + this.format(this.from) + ' – ' + this.format(this.to)
      }
      if (this.from) {
        return 'Periood: alates ' + this.format(this.from)
      }
      if (this.to) {
        return 'Periood: kuni ' + this.format(this.to)
      }
      return 'Periood: kogu ajalugu'
    },
    photoCount () {
      return this.notes.reduce((sum, note) => sum + (note.photos || []).length, 0)
    },
    photosReady () {
      return this.photosLoaded >= this.photoCount
    }
  },
  watch: {
    params: {
      handler () {
        this.refresh()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    safe: sanitizeHtml,
    async refresh () {
      this.loading = true
      this.photosLoaded = 0
      this.round++
      try {
        const [{ object }, { notes }] = await Promise.all([
          api.getObject(this.objectId),
          api.getAllObjectNotes(this.objectId, this.params)
        ])
        this.object = object
        // Whatever the Märkused tab shows, including the töökäsk rows the same
        // endpoint folds in (type 'calendar', description = the work order's
        // comment). They carry no service flag, so the two narrowing filters
        // below drop them on their own — which is the right answer for both.
        this.notes = notes
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.loading = false
    },
    print () {
      // Printing before the photos have decoded leaves blank boxes on the page.
      if (this.includePhotos && !this.photosReady) {
        this.showNotification({ text: 'Fotod laevad veel, proovi hetke pärast uuesti', color: 'orange-darken-2' })
        return
      }
      window.print()
    },
    onPhotoSettled () {
      this.photosLoaded++
    },
    format (date) {
      return dayjs(date).format('DD.MM.YYYY')
    },
    photoUrl (noteId, picture) {
      return '/api/object/' + this.objectId + '/note/' + noteId + '/photo?picture=' + picture
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>

<style lang="scss">
.print-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
}

.doc-header {
  display: flex;
  align-items: center;
  gap: 24px;
  border-bottom: 2px solid #2e7d32;
  padding-bottom: 12px;
  margin-bottom: 16px;

  .doc-logo {
    height: 56px;
  }
  h1 {
    font-size: 22px;
    line-height: 1.2;
    margin: 0;
  }
  .doc-meta {
    font-size: 13px;
    color: #555;
  }
}

.entry {
  padding: 10px 0;
  border-bottom: 1px solid #e0e0e0;
  page-break-inside: avoid;

  .entry-head {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    margin-bottom: 4px;
  }
  .entry-flags,
  .entry-creator {
    color: #555;
  }
  .entry-hidden {
    border: 1px solid #bdbdbd;
    border-radius: 3px;
    padding: 0 4px;
    margin-right: 8px;
    font-size: 12px;
  }
  .entry-body {
    font-size: 14px;
  }
  .entry-photos {
    margin-top: 8px;
  }
  .entry-photo {
    width: 160px;
    height: 160px;
    object-fit: cover;
    margin: 0 8px 8px 0;
    border: 1px solid #ddd;
  }
}

.doc-footer {
  margin-top: 12px;
  font-size: 13px;
  color: #555;
}

.notes {
  i {
    display: block;
  }
  b.title {
    font-size: 15px !important;
    margin: 0 !important;
    padding: 0 !important;
    display: block;
  }
  p {
    margin-bottom: 0px !important;
    margin-top: 0px !important;
  }
  br {
    display: none;
  }
}

@media print {
  .no-print {
    display: none !important;
  }
  .print-page {
    max-width: none;
    padding: 0;
  }
  .v-application,
  .v-main,
  .v-application__wrap {
    background: #fff !important;
    min-height: 0 !important;
  }
  .doc-header {
    // Repeat nothing, but keep the header with the first entries.
    page-break-after: avoid;
  }
  @page {
    margin: 14mm;
  }
}
</style>
